import { useMemo } from 'react';
import { differenceInCalendarDays, isSameDay } from 'date-fns';
import { isBetween } from 'utils/dateTimeUtils';
import { StackedCalendarMode } from 'pages/virtualizedStackedCalendar/Calendar.types';
import { propertiesBusinessType } from 'pages/virtualizedStackedCalendar/Calendar.constants';
import { PropertyBusinessType } from 'models/Properties';
import CalendarBodySelectionService from 'pages/calendar/common/body/selection/CalendarBodySelectionService';
import {
  CalendarSelectionMouseDownHandlerPayload,
  CalendarSelectionMouseEnterHandlerPayload,
  CalendarSelectionMouseUpHandlerPayload,
} from './CalendaBodySelection.types';

function resetSelectionHandler() {
  CalendarBodySelectionService.reset();
  CalendarBodySelectionService.notify();
}

function mouseDownHandler({
  min,
  max,
  limit,
  propertyUid,
  mode,
  visibleProperties,
}: CalendarSelectionMouseDownHandlerPayload) {
  const selectionContainer =
    CalendarBodySelectionService.getSelectionContainer();

  if (!selectionContainer.selection) {
    selectionContainer.selection = {
      limit,
      mouseUpCount: 0,
      min,
      max,
      pivot: min,
      propertyUidPivot: propertyUid,
      propertyUids: [propertyUid],
      mode,
    };

    selectionContainer.visibleProperties = [];

    if (mode === StackedCalendarMode.PRICING) {
      // Prefer the Redux-backed list passed in by the caller — row virtualization unmounts off-screen rows, so the DOM is incomplete. Fall back to the DOM-scrape only when no list was provided (legacy standalone calendar callers).
      if (visibleProperties && visibleProperties.length > 0) {
        visibleProperties.forEach((uid) => {
          const propertyUidSerialized = uid.split('[')[0];
          // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          selectionContainer.visibleProperties.push(propertyUidSerialized);
        });
      } else {
        const elements = document.getElementsByClassName(
          'property-list-item',
        ) as HTMLCollectionOf<HTMLElement>;

        if (!elements.length) {
          // Standalone calendar
          selectionContainer.visibleProperties.push(propertyUid);
        } else {
          for (let i = 0; i < elements.length; i += 1) {
            selectionContainer.visibleProperties.push(
              // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              elements[i].dataset.propertyUid,
            );
          }
        }
      }
    }

    CalendarBodySelectionService.notify();
  }
}

function getPropertiesListRange(
  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  propertyUidPivot,
  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  propertyUid,
  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  visibleProperties,
): string[] {
  const propertyUidPivotSerialized = propertyUidPivot.split('[')[0];
  const propertyUidSerialized = propertyUid.split('[')[0];

  const pivotIndex = visibleProperties.indexOf(propertyUidPivotSerialized);
  const limitIndex = visibleProperties.indexOf(propertyUidSerialized);

  return pivotIndex < limitIndex
    ? visibleProperties.slice(pivotIndex, limitIndex + 1)
    : visibleProperties.slice(limitIndex, pivotIndex + 1);
}

function mouseEnterHandler({
  date,
  propertyUid,
}: CalendarSelectionMouseEnterHandlerPayload) {
  const { selection, visibleProperties } =
    CalendarBodySelectionService.getSelectionContainer();

  if (!selection) {
    return;
  }

  const {
    limit: { start, end },
    pivot,
    propertyUidPivot,
    propertyUids: selectedPropertyUids,
  } = selection;

  if (selection.mode === StackedCalendarMode.PRICING) {
    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    if (!selectedPropertyUids.includes(propertyUid)) {
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      selection.propertyUids.push(propertyUid);
    }

    const newPropertyUids = getPropertiesListRange(
      propertyUidPivot,
      propertyUid,
      visibleProperties,
    );

    selection.propertyUids = newPropertyUids.filter(
      (uid) =>
        propertiesBusinessType[uid]?.type !== PropertyBusinessType.UNIT &&
        propertiesBusinessType[uid]?.type !== PropertyBusinessType.HOTEL,
    );

    CalendarBodySelectionService.notify();
  }

  if (
    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    !selectedPropertyUids.includes(propertyUid) ||
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    !isBetween(date, start, end)
  ) {
    return;
  }

  // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const pivotDiff = differenceInCalendarDays(pivot, date);

  if (pivotDiff === 0) {
    selection.min = date;
    selection.max = date;
    CalendarBodySelectionService.notify();
  } else if (pivotDiff > 0) {
    selection.min = date;
    selection.max = pivot;
    CalendarBodySelectionService.notify();
  } else if (pivotDiff < 0) {
    selection.min = pivot;
    selection.max = date;
    CalendarBodySelectionService.notify();
  }
}

function mouseUpHandler({
  actionCallback,
  date,
}: CalendarSelectionMouseUpHandlerPayload) {
  const { selection } = CalendarBodySelectionService.getSelectionContainer();

  if (!selection) {
    return;
  }

  const { mouseUpCount, min, max, pivot, propertyUids } = selection;

  // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  if (mouseUpCount > 0 || !isSameDay(date, pivot)) {
    actionCallback({
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      from: min,
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      to: max,
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      propertyUids,
    });
  } else {
    selection.mouseUpCount = mouseUpCount + 1;
  }
}

const useCalendarBodySelection = () => {
  return useMemo(
    () => ({
      resetSelection: () => {
        resetSelectionHandler();
      },
      handleMouseDown: (payload: CalendarSelectionMouseDownHandlerPayload) => {
        mouseDownHandler(payload);
      },
      handleMouseEnter: (
        payload: CalendarSelectionMouseEnterHandlerPayload,
      ) => {
        mouseEnterHandler(payload);
      },
      handleMouseUp: (payload: CalendarSelectionMouseUpHandlerPayload) => {
        mouseUpHandler(payload);
      },
      updateSelectionDates: (min: Date, max: Date) => {
        CalendarBodySelectionService.updateSelectionMinMaxDates(min, max);
      },
    }),
    [],
  );
};

export default useCalendarBodySelection;
